import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronsUpDown, Search, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

/**
 * Combobox de productos con búsqueda server-side (debounced).
 * Devuelve el producto seleccionado vía onPick — NO mantiene value (uso "one-shot":
 * seleccionas y se agrega al carrito).
 */
export default function ProductCombobox({
  onPick,                     // (product) => void — agrega al carrito
  placeholder = "Buscar producto por SKU, código o nombre…",
  disabled = false,
  testid = "product-combobox",
  autoFocus = false,
  warehouseId,                // opcional: filtrar por almacén (afecta stock mostrado)
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (!query.trim()) { setItems([]); return; }
    const t = setTimeout(() => {
      setLoading(true);
      api.get("/products", {
        params: {
          q: query,
          in_sku: true, in_name: true, in_barcode: true, in_description: false,
          limit: 25, only_active: true,
        },
      })
        .then(({ data }) => {
          // Puede venir como array o paginado
          const list = Array.isArray(data) ? data : (data?.items || []);
          setItems(list);
          setHighlight(0);
        })
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [query, open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const pick = (p, unit = null) => {
    // Iter 40 — si unit es null usamos la unidad base; si no, aplicamos ratio/price alternos
    if (unit) {
      onPick?.({
        ...p,
        _selected_sale_unit: unit.unit,
        _selected_sale_ratio: Number(unit.ratio),
        _selected_price: Number(unit.price),
      });
    } else {
      onPick?.(p);
    }
    setQuery("");
    setItems([]);
    setTimeout(() => inputRef.current?.focus(), 20);
  };

  // Expandir cada producto en 1 fila base + N filas por unidad alterna activa
  const expanded = React.useMemo(() => {
    const out = [];
    items.forEach((p) => {
      out.push({ ...p, _kind: "base" });
      (p.sale_units || []).forEach((u, i) => {
        if (u && u.active !== false && u.unit && Number(u.ratio) > 0) {
          out.push({ ...p, _kind: "alt", _alt: u, _altIndex: i });
        }
      });
    });
    return out;
  }, [items]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, expanded.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const row = expanded[highlight];
      if (row) pick(row, row._kind === "alt" ? row._alt : null);
    } else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <Popover open={open && !disabled} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          className="w-full justify-between h-10 font-normal text-muted-foreground"
          data-testid={testid}
          autoFocus={autoFocus}
        >
          <span className="flex items-center gap-2 truncate">
            <Package className="w-4 h-4" />
            {placeholder}
          </span>
          <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] max-w-none" align="start">
        <div className="p-2 border-b border-border">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-muted-foreground shrink-0" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="SKU, código de barras o nombre…"
              className="h-8 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0"
              data-testid={`${testid}-search`}
            />
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto" data-testid={`${testid}-list`}>
          {loading && (
            <div className="p-3 text-center text-xs text-muted-foreground">Buscando…</div>
          )}
          {!loading && query.trim() && expanded.length === 0 && items.length === 0 && (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Sin resultados. Prueba con otro término.
            </div>
          )}
          {!loading && !query.trim() && (
            <div className="p-4 text-center text-xs text-muted-foreground">
              Escribe para buscar. Enter agrega el primer resultado.
            </div>
          )}
          {!loading && expanded.map((p, i) => {
            const isAlt = p._kind === "alt";
            const alt = p._alt;
            const stockBase = Number(p.stock ?? 0);
            // Stock disponible en la unidad seleccionada
            const stockAvail = isAlt
              ? Math.floor(stockBase * Number(alt.ratio) * 100) / 100
              : stockBase;
            const noStock = stockAvail <= 0;
            const displayName = isAlt
              ? `${p.name} · ${alt.unit}`
              : p.name;
            const displayPrice = isAlt ? Number(alt.price) : Number(p.price || 0);
            const unitLabel = isAlt ? alt.unit : (p.unit || "UND");
            return (
              <button
                key={`${p.id}-${p._kind}-${p._altIndex ?? ""}`}
                type="button"
                onClick={() => pick(p, isAlt ? alt : null)}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full text-left p-2.5 flex items-start gap-2 border-b border-border/40 last:border-b-0 ${
                  i === highlight ? "bg-accent" : "hover:bg-accent/60"
                } ${isAlt ? "pl-8 bg-muted/30" : ""}`}
                data-testid={`${testid}-option-${p.id}${isAlt ? `-${alt.unit}` : ""}`}
              >
                <Check className="w-4 h-4 mt-0.5 shrink-0 opacity-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    {isAlt && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-primary/10 text-primary rounded uppercase">Suelto</span>}
                    <span className="font-semibold text-sm truncate">{displayName}</span>
                    <span className="data-cell text-[10px] text-muted-foreground">{p.sku}</span>
                  </div>
                  <div className="text-xs flex gap-3 mt-0.5 flex-wrap">
                    {!isAlt && p.barcode && <span className="data-cell text-muted-foreground">CB {p.barcode}</span>}
                    {p.category && !isAlt && <span className="text-muted-foreground">{p.category}</span>}
                    <span className={`data-cell font-bold ${noStock ? "text-destructive" : ""}`}>
                      Stock: {stockAvail} {unitLabel}
                    </span>
                    <span className="data-cell font-bold text-primary ml-auto">
                      RD${displayPrice.toLocaleString("es-DO", { minimumFractionDigits: 2 })}
                      {isAlt && <span className="font-normal text-muted-foreground"> / {alt.unit}</span>}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
